// Story 5.2 — "List for sale" dialog for an opened CERTIFICATE, mounted on
// ShareDetailPage. Thin wrapper over the shared <ListingDialog> (all UI +
// form/KYC/error logic lives there) — this file only wires the certificate-
// specific hooks + config:
//   * floor price bucketed by creator + tier + signed status
//   * €0.10 minimum (FR41)
//   * create-share-listing mutation + ["share-detail"] invalidation
//   * Story 5.6 undo toast (kind: "share")
//
// Its sibling ListSealedDossierForSaleDialog wires the sealed-dossier variant.

import { useQueryClient } from "@tanstack/react-query";

import { SHARE_LISTING_PRICE_MIN_CENTS } from "./feeMath";
import { ListingDialog } from "./ListingDialog";
import { showUndoListingToast } from "./showUndoListingToast";
import { useCreateShareListing } from "./useCreateShareListing";
import {
  useShareTierFloorPrice,
  type ShareTier,
} from "./useShareTierFloorPrice";

export interface ListForSaleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareId: string;
  creatorUserId: string;
  tier: ShareTier;
  /** Whether the share being listed is signed — the floor autopop buckets by
   *  signed status (a signed cert's floor differs from an unsigned one's).
   *  Optional (defaults false) so tests / older callers keep compiling. */
  signed?: boolean;
}

export function ListForSaleDialog({
  open,
  onOpenChange,
  shareId,
  creatorUserId,
  tier,
  signed = false,
}: ListForSaleDialogProps) {
  const queryClient = useQueryClient();
  const floorPriceQuery = useShareTierFloorPrice({
    creatorUserId,
    tier,
    signed,
    enabled: open,
  });
  const mutation = useCreateShareListing();

  return (
    <ListingDialog
      open={open}
      onOpenChange={onOpenChange}
      itemNoun="certificate"
      idPrefix="list"
      minCents={SHARE_LISTING_PRICE_MIN_CENTS}
      floorScopeLabel="creator + tier"
      alreadyExistsMessage="This certificate already has an active listing. Refresh to see current state."
      floorPriceCents={floorPriceQuery.data?.floor_price_cents}
      isSubmitting={mutation.isPending}
      createListing={(args) =>
        mutation.mutateAsync({ share_id: shareId, ...args })
      }
      onListed={(listingId) => {
        // Story 5.6 — 5-second "Undo" affordance (UX-DR8 + L1314/L1770).
        showUndoListingToast({
          kind: "share",
          listingId,
          invalidationKeys: [["share-detail", shareId]],
          queryClient,
        });
        void queryClient.invalidateQueries({
          queryKey: ["share-detail", shareId],
        });
      }}
      onAlreadyExists={() =>
        void queryClient.invalidateQueries({
          queryKey: ["share-detail", shareId],
        })
      }
    />
  );
}
